if (!customElements.get('product-bundle')) {
  customElements.define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      constructor() {
        super();

        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.spinner = this.querySelector('.loading__spinner');
        this.countElement = this.querySelector('[data-bundle-count]');
        this.totalElement = this.querySelector('[data-bundle-total]');
        this.compareElement = this.querySelector('[data-bundle-total-compare]');
        this.errorElement = this.querySelector('[data-bundle-error]');
        this.cartDrawer = document.querySelector('cart-drawer');

        this.onChangeHandler = this.onChange.bind(this);
        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.onChangeHandler);
        this.submitButton?.addEventListener('click', this.onSubmitHandler);
        this.updateState();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChangeHandler);
        this.submitButton?.removeEventListener('click', this.onSubmitHandler);
      }

      onChange(event) {
        const item = event.target.closest('[data-bundle-item]');
        if (item) this.syncItemState(item);
        this.hideError();
        this.updateState();
      }

      onSubmit(event) {
        event.preventDefault();

        const selectedItems = this.getSelectedItems();
        if (!selectedItems.length || !this.submitButton) return;

        this.hideError();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('disabled', 'disabled');
        this.spinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(
          this.cartDrawer
            ? {
                items: selectedItems,
                sections: this.cartDrawer.getSectionsToRender().map((section) => section.id),
                sections_url: window.location.pathname,
              }
            : { items: selectedItems }
        );

        if (this.cartDrawer) this.cartDrawer.setActiveElement(document.activeElement);

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || 'Unable to add these items right now.');
              return;
            }

            if (this.cartDrawer) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-bundle',
                  productVariantId: selectedItems[0].id,
                  cartData: response,
                });
              }
              this.cartDrawer.renderContents(response);
              return;
            }

            window.location = window.routes?.cart_url || routes.cart_url;
          })
          .catch((error) => {
            console.error(error);
            this.showError('Something went wrong while adding your bundle.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.spinner?.classList.add('hidden');
            this.updateState();
          });
      }

      getBundleItems() {
        return Array.from(this.querySelectorAll('[data-bundle-item]'));
      }

      getSelectedItems() {
        return this.getBundleItems()
          .filter((item) => {
            const toggle = item.querySelector('[data-bundle-toggle]');
            return toggle && toggle.checked && !toggle.disabled;
          })
          .map((item) => {
            const variant = this.getVariantData(item);
            return variant.available ? { id: Number(variant.id), quantity: 1 } : null;
          })
          .filter(Boolean);
      }

      getVariantInput(item) {
        return item.querySelector('[data-bundle-variant]');
      }

      getVariantData(item) {
        const input = this.getVariantInput(item);
        if (!input) {
          return { id: 0, price: 0, comparePrice: 0, available: false };
        }

        if (input.tagName === 'SELECT') {
          const selectedOption = input.options[input.selectedIndex];
          return {
            id: selectedOption?.value || 0,
            price: Number(selectedOption?.dataset.price || 0),
            comparePrice: Number(selectedOption?.dataset.comparePrice || 0),
            available: selectedOption?.dataset.available !== 'false',
          };
        }

        return {
          id: input.value || 0,
          price: Number(input.dataset.price || 0),
          comparePrice: Number(input.dataset.comparePrice || 0),
          available: input.dataset.available !== 'false',
        };
      }

      syncItemState(item) {
        const variant = this.getVariantData(item);
        const toggle = item.querySelector('[data-bundle-toggle]');
        const priceElement = item.querySelector('[data-bundle-price]');
        const compareElement = item.querySelector('[data-bundle-compare]');
        const statusElement = item.querySelector('[data-bundle-status]');
        const showComparePrice = this.dataset.showComparePrice === 'true';

        if (priceElement) priceElement.textContent = this.formatMoney(variant.price);

        if (compareElement) {
          if (showComparePrice && variant.comparePrice > variant.price) {
            compareElement.textContent = this.formatMoney(variant.comparePrice);
            compareElement.classList.remove('hidden');
          } else {
            compareElement.textContent = '';
            compareElement.classList.add('hidden');
          }
        }

        if (toggle) {
          toggle.disabled = !variant.available;
          if (!variant.available) toggle.checked = false;
        }

        if (statusElement) {
          statusElement.textContent = variant.available ? 'Included' : 'Sold out';
          statusElement.classList.toggle('product-bundle__status--available', variant.available);
          statusElement.classList.toggle('product-bundle__status--soldout', !variant.available);
        }
      }

      updateState() {
        const showComparePrice = this.dataset.showComparePrice === 'true';
        let total = 0;
        let compareTotal = 0;
        let selectedCount = 0;

        this.getBundleItems().forEach((item) => {
          this.syncItemState(item);

          const toggle = item.querySelector('[data-bundle-toggle]');
          const variant = this.getVariantData(item);
          if (!toggle || !toggle.checked || toggle.disabled || !variant.available) return;

          total += variant.price;
          compareTotal += variant.comparePrice > variant.price ? variant.comparePrice : variant.price;
          selectedCount += 1;
        });

        if (this.countElement) this.countElement.textContent = `${selectedCount}`;
        if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);

        if (this.compareElement) {
          if (showComparePrice && compareTotal > total) {
            this.compareElement.textContent = this.formatMoney(compareTotal);
            this.compareElement.classList.remove('hidden');
          } else {
            this.compareElement.textContent = '';
            this.compareElement.classList.add('hidden');
          }
        }

        if (this.submitButton) {
          const shouldDisable = selectedCount === 0 || this.submitButton.classList.contains('loading');
          this.submitButton.toggleAttribute('disabled', shouldDisable);
        }
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);

        const value = Number(cents || 0) / 100;
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'GBP',
        }).format(value);
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }
    }
  );
}
